import { useState } from "react";
import { Loader2, Repeat } from "lucide-react";

// =====================================================================
// MultiVisitPaintDialog — fires when a dentist paints a multi-visit
// procedure (RCT, crown, denture) in chairside "Today" mode. Instead
// of silently recording it as single-visit "today" work, we ask where
// it should go. Guided Freedom: the plan is the recommended path, but
// the dentist can always say "Finished it all today" (single-
// visit collapse) or cancel.
//
// The visit count is a SOFT estimate — the dentist decides when the
// treatment is actually complete, so the copy never promises a fixed
// number of visits.
// =====================================================================
export default function MultiVisitPaintDialog({
  procName,
  fdi,
  surface,
  onStartBundle,
  onSetupPlan,
  onDoAllToday,
  onCancel,
}) {
  const [busy, setBusy] = useState(false);
  const where = fdi
    ? (surface ? `tooth #${fdi} surface ${surface}` : `tooth #${fdi}`)
    : null;

  async function run(handler) {
    setBusy(true);
    try {
      await handler?.();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog" aria-modal="true" aria-labelledby="multivisit-paint-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4"
      onClick={() => !busy && onCancel?.()}
      data-testid="multivisit-paint-dialog"
      data-tutorial-id="multivisit-paint-dialog"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden"
      >
        <div className="px-5 py-4 border-b border-slate-100 flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-violet-50 flex items-center justify-center shrink-0">
            <Repeat className="w-4 h-4 text-violet-600" />
          </div>
          <div className="min-w-0">
            <p id="multivisit-paint-title" className="text-sm font-semibold text-slate-900">
              <span className="font-semibold text-slate-900">{procName}</span> usually takes more than one visit
            </p>
            <p className="text-[12px] text-slate-500 mt-0.5">
              Most patients come back to finish this{where ? ` on ${where}` : ""}. Record today's visit
              and keep tracking it until it's done — you decide when it's complete. Or, if it was all
              finished today, record it as one visit.
            </p>
          </div>
        </div>
        <div className="px-5 py-4 space-y-2">
          {typeof onStartBundle === "function" ? (
            <button
              type="button"
              onClick={() => run(onStartBundle)}
              disabled={busy}
              data-testid="multivisit-paint-start-bundle"
              data-tutorial-id="multivisit-paint-start-bundle"
              className="w-full px-3 py-2.5 text-sm font-semibold bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-40 inline-flex items-center justify-center gap-1.5"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Repeat className="w-3.5 h-3.5" />}
              Start multi-visit today
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => run(onSetupPlan)}
            disabled={busy}
            data-testid="multivisit-paint-setup-plan"
            data-tutorial-id="multivisit-paint-setup-plan"
            className="w-full px-3 py-2.5 text-sm font-semibold border border-slate-200 rounded-lg text-slate-700 hover:bg-slate-50 disabled:opacity-40"
          >
            Propose as Plan
          </button>
          <button
            type="button"
            onClick={() => run(onDoAllToday)}
            disabled={busy}
            data-testid="multivisit-paint-do-today"
            data-tutorial-id="multivisit-paint-do-today"
            className="w-full px-3 py-2.5 text-sm font-semibold border border-slate-200 rounded-lg text-slate-700 hover:bg-slate-50 disabled:opacity-40"
          >
            Finished it all today
          </button>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end">
          <button
            type="button" onClick={() => !busy && onCancel?.()} disabled={busy}
            data-testid="multivisit-paint-cancel"
            data-tutorial-id="multivisit-paint-cancel"
            className="px-3 py-2 text-xs font-semibold border border-slate-200 rounded text-slate-600 hover:bg-slate-50 disabled:opacity-40"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
